import { memo } from 'react'

import { cn } from '@/lib/utils'

import type { Job } from '../types'
import type { JobDisplayStatus } from '../utils/job-status'
import { JOB_STATUS_VISUAL } from './status-meta'

/** Numbered pin; colour + icon glyph + number so status is not conveyed by colour alone. */
export const JobMarker = memo(function JobMarker({
  job,
  number,
  display,
  isSelected,
  dimmed = false,
  onSelect,
}: {
  job: Job
  number: number
  display: JobDisplayStatus
  isSelected: boolean
  dimmed?: boolean
  onSelect: (jobId: string) => void
}) {
  const visual = JOB_STATUS_VISUAL[display.kind]
  const Icon = visual.icon
  const size = isSelected ? 44 : 32

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onSelect(job.id)
      }}
      aria-label={`Job ${number}: ${job.bookingNo}, ${job.service}, ${display.label}. Select job`}
      aria-pressed={isSelected}
      className={cn(
        'group relative block transition-opacity focus-visible:outline-none',
        dimmed && 'opacity-[0.35]',
      )}
      style={{ width: size, height: size * 1.25 }}
    >
      <svg viewBox="0 0 32 40" width={size} height={size * 1.25} className="drop-shadow-md" aria-hidden="true">
        <path
          d="M16 0C7.2 0 0 7.1 0 15.9 0 27.8 16 40 16 40s16-12.2 16-24.1C32 7.1 24.8 0 16 0z"
          fill={visual.hex}
          stroke="white"
          strokeWidth={isSelected ? 3 : 2}
        />
      </svg>
      <span className="absolute inset-x-0 top-[5px] flex flex-col items-center leading-none text-white" aria-hidden="true">
        <span className={cn('font-bold tabular-nums', isSelected ? 'text-[13px]' : 'text-[11px]')}>{number}</span>
        <Icon className={cn(isSelected ? 'mt-0.5 h-3 w-3' : 'mt-px h-2.5 w-2.5')} />
      </span>
      <span className="pointer-events-none absolute inset-0 rounded-full group-focus-visible:ring-4 group-focus-visible:ring-[#169b62]/60" aria-hidden="true" />
    </button>
  )
})

export const ClusterMarker = memo(function ClusterMarker({
  count,
  kind,
  label,
  dimmed = false,
  onZoom,
}: {
  count: number
  kind: 'workers' | 'jobs'
  label: string
  dimmed?: boolean
  onZoom: () => void
}) {
  return (
    <div style={{ transform: 'translateY(50%)' }} className={cn('transition-opacity', dimmed && 'opacity-[0.35]')}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onZoom()
        }}
        aria-label={`${count} ${kind} here: ${label}. Zoom in`}
        className={cn(
          'flex h-10 min-w-10 items-center justify-center rounded-full border-[3px] border-white px-2 text-[13px] font-bold text-white shadow-lg',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#169b62]/60',
          kind === 'workers' ? 'bg-[#169b62]' : 'bg-amber-500',
        )}
      >
        {count}
      </button>
    </div>
  )
})
